import {
  ActivitiesTab,
  ActivityTabToType,
  ChromeExtensionRequestType,
  DEFAULT_FILTER_TYPES,
} from 'app/pages/loanApplication/tabs/activities/Activities.constants';
import {
  activitiesApi,
  callApi,
  emailNotificationsNotificationsApi,
  emailSnippetsApi,
} from 'app/services/api';
import { normalize, schema } from 'normalizr';

import { ActivityType, EMAILS_INCLUDE_CONFIG, EmailStateType } from 'constants/activities';
import { CALL_EXTENSION_ID } from 'constants/extension';
import { FeatureFlag } from 'constants/featureFlags';
import { LOAN_APPLICATION_ACTION_NAME_PREFIX } from 'constants/loanApplication';
import { catchAsyncActionError } from 'utils/action';
import { emailContentToString, mapCallActivities, mapEmailActivities } from 'utils/activities';
import { blobToBase64 } from 'utils/file';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { getUuid } from 'store/auth/selectors';
import { openConfirmationDialogAction } from 'store/global/actions';
import { selectFeatureFlagByKey } from 'store/global/selectors';

import { selectEmailsCount } from '../selectors';

import { getApplicationContactsAction, resolveActivityContactsAction } from './contacts.actions';

const activitiesSchema = new schema.Entity(
  'activities',
  {},
  {
    idAttribute: ({ id, callHistoryId, type, activityDateTime }) =>
      id || callHistoryId || `${type}-${activityDateTime}`,
  },
);

export const ACTIVITIES_ACTION_TYPE = createNamedActions(
  // LOAN_APPLICATION_ACTION_NAME_PREFIX is used for Redux Middleware in Loan Editor
  `${LOAN_APPLICATION_ACTION_NAME_PREFIX}/activities`,
  {
    GET_ACTIVITIES: 'GET_ACTIVITIES',
    GET_EMAIL_ACTIVITIES: 'GET_EMAIL_ACTIVITIES',
    UPDATE_ACTIVITIES: 'UPDATE_ACTIVITIES',
    PATCH_ACTIVITIES: 'PATCH_ACTIVITIES',
    RESET_ACTIVITIES: 'RESET_ACTIVITIES',
    OPEN_CALL_IN_EXTENSION: 'OPEN_CALL_IN_EXTENSION',
    GET_EMAIL_SNIPPETS: 'GET_EMAIL_SNIPPETS',
    GET_GENERATED_SNIPPET: 'GET_GENERATED_SNIPPET',
    GET_ALL_RESOURCES: 'GET_ALL_RESOURCES',
  },
);

export const getEmailActivities = catchAsyncActionError(
  ({ applicationId }) =>
    async (dispatch, getState) => {
      const state = getState();
      const currentUserId = getUuid(state);
      const emailCount = selectEmailsCount(state);

      let retryCount = 0;
      let fetchedEmails = [];

      while (fetchedEmails?.length <= emailCount && retryCount < 5) {
        const requestData = await emailNotificationsNotificationsApi.getEmails({
          metadata: `ApplicationId:${applicationId}`,
          include: EMAILS_INCLUDE_CONFIG,
        });

        if (requestData?.data?.length > emailCount) {
          fetchedEmails = requestData?.data;
          break;
        }

        await new Promise(res => setTimeout(res, 2000));

        retryCount++;
      }

      const mappedEmails = mapEmailActivities(fetchedEmails, currentUserId);

      const emailActivitiesAction = dispatch({
        type: ACTIVITIES_ACTION_TYPE.GET_EMAIL_ACTIVITIES,
        payload: normalize(mappedEmails, [activitiesSchema]).entities.activities,
      });

      dispatch(resolveActivityContactsAction());

      return emailActivitiesAction;
    },
);

// CALL activity type should be requested by separate request directly to telephony api.
// Common update events are requested only on ALL tab via skynet api. EMAILS requested by notifications api
// All tab - 3 requests (CommonUpdate, Calls, Emails), CALLS tab = calls request, EMAILS tab = emails request
const getActivitiesByApplicationIdAction = catchAsyncActionError(
  ({
    applicationId,
    types,
    isCallsTabActive,
    isAllTabActive,
    shouldRequestCalls,
    signal,
    searchTerm,
  }) =>
    async (dispatch, getState) => {
      const shouldRequestEmails = !isCallsTabActive && types.includes(ActivityType.EMAIL);

      const commonActivitiesPromise = isAllTabActive
        ? activitiesApi.getActivitiesByApplicationId({
            applicationId,
            types: DEFAULT_FILTER_TYPES,
            signal,
          })
        : Promise.resolve();

      const emailActivitiesPromise = shouldRequestEmails
        ? emailNotificationsNotificationsApi.getEmails({
            metadata: `ApplicationId:${applicationId}`,
            include: EMAILS_INCLUDE_CONFIG,
            signal,
            searchTerm,
          })
        : Promise.resolve();

      const callActivitiesPromise = shouldRequestCalls
        ? callApi.getCallActivities({ applicationId, signal })
        : Promise.resolve();

      const [emailActivitiesResult, callActivitiesResult, commonActivitiesResult] =
        await Promise.allSettled([
          emailActivitiesPromise,
          callActivitiesPromise,
          commonActivitiesPromise,
        ]);

      const currentUserId = getUuid(getState());

      const emailActivities = mapEmailActivities(emailActivitiesResult?.value?.data, currentUserId);

      const callActivities = mapCallActivities(callActivitiesResult?.value?.data);

      const combinedData = emailActivities.concat(
        callActivities,
        commonActivitiesResult?.value?.data,
      );

      return dispatch({
        type: ACTIVITIES_ACTION_TYPE.GET_ACTIVITIES,
        payload: normalize(combinedData, [activitiesSchema]).entities.activities,
      });
    },
);

export const updateCallNotesAction = catchAsyncActionError(params => async dispatch => {
  await callApi.updateCallNotes(params);

  const { callHistoryId, callNotes } = params;

  return dispatch({
    type: ACTIVITIES_ACTION_TYPE.PATCH_ACTIVITIES,
    payload: {
      ids: [callHistoryId],
      patches: { [callHistoryId]: { callNotes } },
    },
  });
});

export const updateCallOutcomeAction = catchAsyncActionError(params => async dispatch => {
  await callApi.updateCallOutcome(params);

  const { callHistoryId, callDisposition } = params;

  return dispatch({
    type: ACTIVITIES_ACTION_TYPE.PATCH_ACTIVITIES,
    payload: {
      ids: [callHistoryId],
      patches: { [callHistoryId]: { callDisposition } },
    },
  });
});

export const readEmailsAction = catchAsyncActionError(ids => async dispatch => {
  await emailNotificationsNotificationsApi.readEmails([
    {
      ids,
      type: EmailStateType.READ,
    },
  ]);

  const patches = ids.reduce((obj, id) => {
    obj[id] = { isNewEmail: false };
    return obj;
  }, {});

  return dispatch({
    type: ACTIVITIES_ACTION_TYPE.PATCH_ACTIVITIES,
    payload: { ids, patches },
  });
});

export const getEmailBodyAction = catchAsyncActionError(id => async (dispatch, getState) => {
  const state = getState();

  const isLightWeightEmailBodyAllowed = selectFeatureFlagByKey(
    FeatureFlag.EnableLightWeightEmailBody,
  )(state);

  const { data } = await emailNotificationsNotificationsApi.getEmails({ id, include: 'body' });

  const body = isLightWeightEmailBodyAllowed
    ? data[0]?.emailBody?.Content?.value
    : data[0]?.body?.value;

  const bodyString = emailContentToString(body);

  return dispatch({
    type: ACTIVITIES_ACTION_TYPE.PATCH_ACTIVITIES,
    payload: {
      ids: [id],
      patches: { [id]: { body, ...(!isLightWeightEmailBodyAllowed ? { bodyString } : {}) } },
    },
  });
});

export const removeActivitiesAction = () => dispatch =>
  dispatch({
    type: ACTIVITIES_ACTION_TYPE.RESET_ACTIVITIES,
  });

// invokes chrome Call Extenstion (so works only with chrome) and immediatelly starts a call
export const extensionMakeCallAction =
  ({ applicationId, fullName, companyId, phoneNumber }) =>
  dispatch => {
    // eslint-disable-next-line no-undef
    if (chrome) {
      // eslint-disable-next-line no-undef
      chrome.runtime.sendMessage(CALL_EXTENSION_ID, {
        applicationId,
        companyId,
        leadNumberToDialOut: phoneNumber,
        customerName: fullName,
        type: ChromeExtensionRequestType.START_CALL,
      });
    }

    return dispatch({ type: ACTIVITIES_ACTION_TYPE.OPEN_CALL_IN_EXTENSION });
  };

export const makeCallWithDialogAction =
  ({ applicationId, fullName, companyId, phoneNumber }) =>
  dispatch => {
    dispatch(
      openConfirmationDialogAction({
        message: 'Confirm starting new call',
        description: 'Are you sure you want to start new call?',
        onAccept: () => {
          dispatch(extensionMakeCallAction({ applicationId, phoneNumber, fullName, companyId }));
        },
      }),
    );
  };

export const getGeneratedEmailSnippet = catchAsyncActionError(
  ({ snippetId, applicationId }) =>
    async dispatch => {
      const { data } = await emailSnippetsApi.getGeneratedSnippet({ snippetId, applicationId });

      dispatch({ type: ACTIVITIES_ACTION_TYPE.GET_GENERATED_SNIPPET });

      return data;
    },
);

export const getAllResourcesAction =
  (attachmentNames, linkedResourceIds, emailId) => async dispatch => {
    const attachmentsPromise = attachmentNames?.length
      ? Promise.all(
          attachmentNames.map(name =>
            emailNotificationsNotificationsApi.getAttachment({
              emailId,
              attachmentName: name,
            }),
          ),
        )
      : Promise.resolve([]);

    const linkedResourcesPromise = linkedResourceIds.length
      ? Promise.all(
          linkedResourceIds.map(resourceId =>
            emailNotificationsNotificationsApi.getLinkedResource({
              emailId,
              resourceId,
            }),
          ),
        )
      : Promise.resolve([]);

    const attachmentsBinary = await attachmentsPromise;
    const linkedResourcesBinary = await linkedResourcesPromise;

    const attachments = [];
    for (const [index, attachmentBinary] of Object.entries(attachmentsBinary)) {
      const base64String = await blobToBase64(attachmentBinary.data);
      attachments.push({
        size: attachmentBinary.data.size,
        base64String,
        name: decodeURIComponent(attachmentNames[index]),
      });
    }

    const linkedResources = [];
    for (const linkedResourceBinary of linkedResourcesBinary) {
      const base64String = await blobToBase64(linkedResourceBinary.data);
      linkedResources.push({
        size: linkedResourceBinary.data.size,
        base64String,
      });
    }

    return dispatch({
      type: ACTIVITIES_ACTION_TYPE.GET_ALL_RESOURCES,
      payload: { attachments, linkedResources },
    });
  };

export const updateActivitiesAction =
  ({
    applicationId,
    isAllTabActive,
    filteredTypes,
    applicationContactsCallback = () => {},
    activeTab,
    isManageCallsAllowed,
    searchTerm,
    signal,
  }) =>
  async dispatch => {
    if (filteredTypes.length || !isAllTabActive) {
      const applicationContactsRequest = dispatch(
        getApplicationContactsAction({ applicationId }),
      ).finally(applicationContactsCallback);

      const filteredTypesForRequest = filteredTypes.length
        ? DEFAULT_FILTER_TYPES.concat(filteredTypes)
        : [];

      const isCallsTabActive = activeTab === ActivitiesTab.CALLS;

      const shouldRequestCalls =
        isManageCallsAllowed &&
        (isCallsTabActive || (isAllTabActive && filteredTypes.includes(ActivityType.CALL)));

      await dispatch(
        getActivitiesByApplicationIdAction({
          applicationId,
          types: isAllTabActive ? filteredTypesForRequest : [ActivityTabToType[activeTab]],
          isAllTabActive,
          shouldRequestCalls,
          isCallsTabActive,
          searchTerm,
          signal,
        }),
      ).catch(Error);

      // Names resolve after the list renders and once application contacts are known, so values
      // they already cover are not looked up again.
      Promise.allSettled([applicationContactsRequest]).then(() =>
        dispatch(resolveActivityContactsAction()),
      );
    }

    return dispatch({
      type: ACTIVITIES_ACTION_TYPE.UPDATE_ACTIVITIES,
    });
  };
